import type { ReactNode } from 'react';

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

export function AdminPage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={joinClasses('mx-auto max-w-7xl space-y-6 px-6 py-6 md:px-8 md:py-8', className)}>
      {children}
    </div>
  );
}

export function AdminPageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        {eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--text-tertiary)]">
            {eyebrow}
          </p>
        ) : null}
        <h1 className={joinClasses('text-3xl font-semibold tracking-tight text-[var(--text-primary)]', eyebrow ? 'mt-2' : '')}>
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--text-secondary)]">
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex items-center gap-3">{actions}</div> : null}
    </header>
  );
}

export function AdminPanel({
  title,
  subtitle,
  children,
  className,
  rightSlot,
  contentClassName,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  rightSlot?: ReactNode;
  contentClassName?: string;
}) {
  return (
    <section
      className={joinClasses(
        'overflow-hidden rounded-[1.5rem] border border-[var(--border-default)] bg-white shadow-[0_12px_28px_rgba(15,23,42,0.05)]',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] bg-white px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h2>
          {subtitle ? (
            <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{subtitle}</p>
          ) : null}
        </div>
        {rightSlot ? <div className="shrink-0 text-sm text-[var(--text-tertiary)]">{rightSlot}</div> : null}
      </div>
      <div className={joinClasses('px-5 py-5', contentClassName)}>{children}</div>
    </section>
  );
}

export function AdminStatCard({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const borderStyles: Record<string, string> = {
    default: 'border-t-[var(--accent-primary)]',
    success: 'border-t-[var(--status-success)]',
    warning: 'border-t-[var(--status-warning)]',
    danger: 'border-t-[var(--status-error)]',
  };

  return (
    <div className={joinClasses('rounded-[1.4rem] border border-[var(--border-default)] border-t-4 bg-white px-5 py-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]', borderStyles[tone])}>
        <p className="text-sm font-medium text-[var(--text-secondary)]">{label}</p>
        <p className="mt-2 text-4xl font-semibold tracking-tight text-[var(--text-primary)]">{value}</p>
        <p className="mt-2 text-sm text-[var(--text-tertiary)]">{sub}</p>
    </div>
  );
}

export function AdminMetricTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-[1.15rem] border border-[var(--border-default)] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
      <p className="text-sm font-medium text-[var(--text-secondary)]">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-[var(--text-primary)]">{value}</p>
      <p className="mt-2 text-sm text-[var(--text-tertiary)]">{sub}</p>
    </div>
  );
}

export function AdminTablePanel({
  title,
  subtitle,
  children,
  rightSlot,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  rightSlot?: ReactNode;
}) {
  return (
    <AdminPanel title={title} subtitle={subtitle} rightSlot={rightSlot} contentClassName="p-0">
      <div className="overflow-x-auto">{children}</div>
    </AdminPanel>
  );
}

export function AdminEmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-[1.4rem] border border-dashed border-[var(--border-default)] bg-white px-6 py-12 text-center">
      <p className="text-base font-semibold text-[var(--text-primary)]">{title}</p>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-[var(--text-secondary)]">
        {description}
      </p>
    </div>
  );
}
